import FlaglerCamPlayers from "@/components/Seo/FlaglerCamPlayers";
import FaqSection from "@/components/Seo/FaqSection";
import {
  flaglerInternalLinks,
  flaglerLocationLine,
  flaglerSections,
  type FlaglerLanding,
  type FlaglerReadings,
} from "@/lib/flaglerLandings";
import { aboutReport, type FaqEntry } from "@/lib/seoAnswer";
import { fontStack } from "@/theme";
import { Box, Button, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const bodySx = {
  color: "text.secondary",
  fontSize: { xs: "0.95rem", md: "1rem" },
  lineHeight: 1.7,
  maxWidth: 780,
} as const;

const linkButtonSx = {
  color: "ink.main",
  borderColor: "rule.strong",
  fontSize: "0.8125rem",
  "&:hover": {
    borderColor: "ink.main",
    backgroundColor: "transparent",
  },
} as const;

interface FlaglerArticleProps {
  page: FlaglerLanding;
  readings: FlaglerReadings;
  faqs: FaqEntry[];
  showTitle?: boolean;
  showIntro?: boolean;
  showPlayers?: boolean;
  showAbout?: boolean;
}

export default function FlaglerArticle({
  page,
  readings,
  faqs,
  showTitle = false,
  showIntro = true,
  showPlayers = false,
  showAbout = false,
}: FlaglerArticleProps) {
  const sections = flaglerSections(page, readings);
  const related = flaglerInternalLinks(page.path);

  return (
    <Box component="article" id="seo-content" sx={{ maxWidth: 820 }}>
      {showTitle ? (
        <Typography
          component="h1"
          sx={{
            fontFamily: fontStack.serif,
            fontSize: { xs: "1.45rem", md: "1.85rem" },
            letterSpacing: 0,
            lineHeight: 1.15,
            color: "ink.main",
            mb: 1,
          }}
        >
          {page.h1}
        </Typography>
      ) : null}
      {showIntro ? (
        <Typography sx={{ ...bodySx, mb: 1.25 }}>{page.intro}</Typography>
      ) : null}
      <Typography sx={{ ...bodySx, mb: 2 }}>{flaglerLocationLine(page)}</Typography>
      {sections.map((section) => (
        <Box key={section.id} component="section" id={section.id} sx={{ mb: 2.25 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: fontStack.serif,
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              letterSpacing: 0,
              color: "ink.main",
              mb: 0.75,
            }}
          >
            {section.heading}
          </Typography>
          {section.paragraphs.map((paragraph) => (
            <Typography key={paragraph} sx={{ ...bodySx, mb: 1 }}>
              {paragraph}
            </Typography>
          ))}
          {section.links.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {section.links.map((link) =>
                link.external ? (
                  <Button
                    key={link.href}
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    sx={linkButtonSx}
                  >
                    {link.label}
                  </Button>
                ) : (
                  <Button
                    key={link.href}
                    component={RouterLink}
                    to={link.href}
                    size="small"
                    variant="outlined"
                    sx={linkButtonSx}
                  >
                    {link.label}
                  </Button>
                ),
              )}
            </Box>
          ) : null}
          {showPlayers && section.id === "surf-cam" ? <FlaglerCamPlayers /> : null}
        </Box>
      ))}
      {showAbout ? (
        <Box component="section" sx={{ mb: 2.25 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: fontStack.serif,
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              letterSpacing: 0,
              color: "ink.main",
              mb: 0.75,
            }}
          >
            About this report
          </Typography>
          <Typography sx={bodySx}>{aboutReport("us", page.placeName)}</Typography>
        </Box>
      ) : null}
      {related.length > 0 ? (
        <Box sx={{ mb: 1 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: fontStack.serif,
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              letterSpacing: 0,
              color: "ink.main",
              mb: 1,
            }}
          >
            Related pages
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {related.map((link) => (
              <Button
                key={link.href}
                component={RouterLink}
                to={link.href}
                size="small"
                variant="outlined"
                sx={linkButtonSx}
              >
                {link.label}
              </Button>
            ))}
          </Box>
        </Box>
      ) : null}
      {faqs.length > 0 ? <FaqSection items={faqs} /> : null}
    </Box>
  );
}
